import React, { useMemo, useState, useEffect } from 'react';
import { generateSmartBookingUrls, generateTicketUrl, generateTicketmasterUrl, generateVividSeatsUrl, generateRestaurantUrl } from '../utils/bookingUrls';
import { logEvent } from '../utils/analytics';
import './TabbedItineraryModal.css';

// Helper functions for URL generation
const formatDateForUrl = (dateString) => {
  const date = new Date(dateString);
  return date.toISOString().split('T')[0];
};

const extractCityFromVenue = (venue) => {
  // Common venue patterns and their cities
  const venueToCity = {
    'Mercedes-Benz Stadium': 'Atlanta, GA',
    'Jones AT&T Stadium': 'Lubbock, TX',
    'AT&T Stadium': 'Arlington, TX',
    'Lambeau Field': 'Green Bay, WI',
    'Soldier Field': 'Chicago, IL',
    'MetLife Stadium': 'East Rutherford, NJ',
    'Gillette Stadium': 'Foxborough, MA',
    'Hard Rock Stadium': 'Miami Gardens, FL',
    'Raymond James Stadium': 'Tampa, FL',
    'State Farm Stadium': 'Glendale, AZ',
    'Arrowhead Stadium': 'Kansas City, MO',
    'M&T Bank Stadium': 'Baltimore, MD',
    'FirstEnergy Stadium': 'Cleveland, OH',
    'Lucas Oil Stadium': 'Indianapolis, IN',
    'Ford Field': 'Detroit, MI',
    'U.S. Bank Stadium': 'Minneapolis, MN',
    'Caesars Superdome': 'New Orleans, LA',
    'Empower Field at Mile High': 'Denver, CO',
    'Allegiant Stadium': 'Las Vegas, NV',
    'SoFi Stadium': 'Los Angeles, CA',
    'Levi\'s Stadium': 'Santa Clara, CA',
    'CenturyLink Field': 'Seattle, WA',
    'Bryant-Denny Stadium': 'Tuscaloosa, AL',
    'Ohio Stadium': 'Columbus, OH',
    'Sanford Stadium': 'Athens, GA',
    'Darrell K Royal Stadium': 'Austin, TX',
    'Tiger Stadium': 'Baton Rouge, LA',
    'Michigan Stadium': 'Ann Arbor, MI',
    'Beaver Stadium': 'University Park, PA',
    'Memorial Stadium Lincoln': 'Lincoln, NE',
    'Camp Randall Stadium': 'Madison, WI',
    'Rose Bowl': 'Pasadena, CA',
    'Cotton Bowl': 'Dallas, TX',
    'Orange Bowl': 'Miami, FL',
    'Sugar Bowl': 'New Orleans, LA',
    'Gaylord Family Stadium': 'Norman, OK',
    'Memorial Stadium Clemson': 'Clemson, SC',
    'Kyle Field': 'College Station, TX',
    'Ben Hill Griffin Stadium': 'Gainesville, FL',
    'Doak Campbell Stadium': 'Tallahassee, FL',
    'Jordan-Hare Stadium': 'Auburn, AL',
    'Neyland Stadium': 'Knoxville, TN',
    'Williams-Brice Stadium': 'Columbia, SC',
    'Vaught-Hemingway Stadium': 'Oxford, MS',
    'Davis Wade Stadium': 'Starkville, MS',
    'Reynolds Razorback Stadium': 'Fayetteville, AR',
    'Donald W. Reynolds Razorback Stadium': 'Fayetteville, AR',
    'Faurot Field': 'Columbia, MO',
    'Memorial Stadium Lawrence': 'Lawrence, KS',
    'Bill Snyder Family Stadium': 'Manhattan, KS',
    'Boone Pickens Stadium': 'Stillwater, OK',
    'Amon G. Carter Stadium': 'Fort Worth, TX',
    'McLane Stadium': 'Waco, TX',
    'Alamodome': 'San Antonio, TX',
    'Sun Devil Stadium': 'Tempe, AZ',
    'Folsom Field': 'Boulder, CO',
    'Rice-Eccles Stadium': 'Salt Lake City, UT',
    'LaVell Edwards Stadium': 'Provo, UT',
    'Albertsons Stadium': 'Boise, ID',
    'Martin Stadium': 'Pullman, WA',
    'Husky Stadium': 'Seattle, WA',
    'Autzen Stadium': 'Eugene, OR',
    'Reser Stadium': 'Corvallis, OR',
    'Stanford Stadium': 'Stanford, CA',
    'California Memorial Stadium': 'Berkeley, CA',
    'Los Angeles Memorial Coliseum': 'Los Angeles, CA',
    'Arizona Stadium': 'Tucson, AZ',
    'Arizona State Sun Devil Stadium': 'Tempe, AZ'
  };

  // Check exact matches first
  if (venueToCity[venue]) {
    return venueToCity[venue];
  }

  // Try partial matches (check for exact venue name match first)
  for (const [venueName, city] of Object.entries(venueToCity)) {
    if (venue.includes(venueName)) {
      return city;
    }
  }

  // Fallback: extract from venue name patterns
  const cityMatches = venue.match(/,\s*([A-Z]{2})/);
  if (cityMatches) {
    const stateCode = cityMatches[1];
    const cityPart = venue.split(',')[0].trim();
    return `${cityPart}, ${stateCode}`;
  }

  // Default fallback
  return venue;
};

const tabs = [
  { id: 'overview', label: 'Overview' },
  { id: 'dining', label: 'Food & Drinks' },
  { id: 'flights', label: 'Flights' },
  { id: 'hotels', label: 'Hotels' },
  { id: 'tickets', label: 'Tickets' },
  { id: 'transport', label: 'Transportation' },
  { id: 'summary', label: 'Summary' }
];

const TabbedItineraryModal = ({ isOpen, onClose, recommendation, game, teamName, teamColors, onOpenBooking, userLocation }) => {
  const [active, setActive] = useState('overview');
  const [costs, setCosts] = useState({ flights: '', hotels: '', tickets: '', other: '' });

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [isOpen]);

  const primary = teamColors?.primary || '#0ea5e9';
  const secondary = teamColors?.secondary || '#f97316';

  // Simple luminance check to ensure readable text on light team colors
  const isLightHex = (hex) => {
    try {
      const h = String(hex || '').replace('#', '');
      if (h.length !== 6) return false;
      const r = parseInt(h.slice(0, 2), 16);
      const g = parseInt(h.slice(2, 4), 16);
      const b = parseInt(h.slice(4, 6), 16);
      const brightness = (r * 299 + g * 587 + b * 114) / 1000;
      return brightness > 200; // treat very light colors as light
    } catch {
      return false;
    }
  };
  const headerTitleColor = isLightHex(primary) ? '#111827' : primary;

  const bookingUrls = useMemo(() => {
    try {
      if (!game || !teamName) return {};
      return generateSmartBookingUrls(game, teamName, userLocation || '');
    } catch {
      return {};
    }
  }, [game, teamName, userLocation]);

  const parseAmount = (val) => {
    const n = parseFloat(String(val || '').replace(/[^0-9.]/g, ''));
    return isNaN(n) ? 0 : n;
  };

  const totalCost = useMemo(() => {
    return parseAmount(costs.flights) + parseAmount(costs.hotels) + parseAmount(costs.tickets) + parseAmount(costs.other);
  }, [costs]);

  const formatCurrency = (n) => {
    try {
      return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n || 0);
    } catch {
      return `$${(n || 0).toFixed(2)}`;
    }
  };

  if (!isOpen || !recommendation || !game) return null;

  const Header = () => (
    <div className="p-4 border-b border-gray-200 flex items-center justify-between">
      <div className="mr-3">
        <h3 className="text-xl font-bold text-gray-900" style={{ color: headerTitleColor }}>{teamName} vs {game.opponent}</h3>
        <p className="text-sm text-gray-600">{game.venue} • {game.kickoff} • {recommendation.gameInfo?.date || game.date}</p>
      </div>
      <button aria-label="Close" onClick={onClose} className="px-2 py-1 rounded-md text-gray-600 hover:text-gray-800 hover:bg-gray-100">✕</button>
    </div>
  );

  const Tabs = () => (
    <div className="tabs sticky top-0 z-10" style={{ borderBottomColor: primary }}>
      <div className="flex overflow-x-auto space-x-2 px-2">
        {tabs.map(t => {
          const isActive = active === t.id;
          return (
            <button
              key={t.id}
              onClick={() => setActive(t.id)}
              className={`tab text-sm ${isActive ? 'active' : ''}`}
              style={isActive ? { background: `linear-gradient(135deg, ${primary} 0%, ${secondary} 100%)`, color: '#fff', border: `1px solid ${primary}` } : {}}
            >
              {t.label}
            </button>
          );
        })}
      </div>
    </div>
  );

  const Section = ({ title, children }) => (
    <div className="mb-6">
      <h4 className="text-lg font-semibold text-gray-900 mb-3">{title}</h4>
      {children}
    </div>
  );

  const Overview = () => {
    const raw = recommendation.rawResponse || '';
    const verified = Boolean(
      recommendation.scheduleVerification?.gameExists === true || /"gameExists"\s*:\s*true/i.test(raw)
    );
    const venueText = recommendation.scheduleVerification?.venue || '';
    return (
      <div className="panel">
        <Section title="Schedule Verification">
          {verified ? (
            <div className="p-3 rounded-lg border bg-green-50 border-green-200 text-green-800 text-sm">
              ✅ Game confirmed{venueText ? ` at ${venueText}` : ''}
            </div>
          ) : (
            <div className="p-3 rounded-lg border bg-amber-50 border-amber-200 text-amber-800 text-sm">
              ⚠️ Unable to verify schedule
            </div>
          )}
        </Section>
      {recommendation.gameInfo?.atmosphere && (
        <Section title="What Makes This Special">
          <div className="p-4 bg-white rounded-lg border border-gray-200 text-gray-800 text-sm">
            {recommendation.gameInfo.atmosphere}
          </div>
        </Section>
      )}
      </div>
    );
  };

  const ListCard = ({ title, body, cta, withAccent = false }) => (
    <div className="border border-gray-200 rounded-xl p-4 bg-white shadow-sm hover:shadow-md transition-shadow duration-200">
      {withAccent && (
        <div className="mb-3 h-1.5 rounded-full" style={{ background: `linear-gradient(135deg, ${primary} 0%, ${secondary} 100%)` }}></div>
      )}
      <h5 className="font-semibold text-gray-900 mb-1">{title}</h5>
      {body && <p className="text-sm text-gray-700 mb-3">{body}</p>}
      {cta}
    </div>
  );

  const Flights = () => (
    <div className="panel grid grid-cols-1 md:grid-cols-2 gap-4">
      {(recommendation.travel?.flights || []).length === 0 && (
        <div className="border border-gray-200 rounded-xl p-4 bg-white text-sm text-gray-600">No flight suggestions provided.</div>
      )}
      {(recommendation.travel?.flights || []).map((f, i) => (
        <ListCard key={i}
          title={f.suggestion || f.partner || 'Flights'}
          body={f.details}
          cta={<button onClick={() => {
            try { logEvent('open_flights_search', { team: teamName, opponent: game?.opponent }); } catch {}
            if (typeof onOpenBooking === 'function') {
              onOpenBooking('flights', game, teamName);
            } else if (bookingUrls.flights) {
              window.open(bookingUrls.flights, '_blank', 'noopener,noreferrer');
            }
          }} className="text-blue-600 text-sm">Search flights</button>}
        />
      ))}
    </div>
  );

  const Hotels = () => (
    <div className="panel grid grid-cols-1 md:grid-cols-2 gap-4">
      {/* Hotels.com */}
      <ListCard
        title="Hotels.com"
        body="Find traditional hotels with free cancellation and best price guarantee"
        cta={<button onClick={() => {
          try { logEvent('open_hotels_search', { team: teamName, opponent: game?.opponent, partner: 'hotels.com' }); } catch {}
          if (typeof onOpenBooking === 'function') {
            onOpenBooking('hotels', game, teamName);
          } else if (bookingUrls.hotels) {
            window.open(bookingUrls.hotels, '_blank', 'noopener,noreferrer');
          }
        }} className="text-blue-600 text-sm">Find hotels</button>}
      />
      
      {/* Travelocity */}
      <ListCard
        title="Travelocity"
        body="Compare hotel rates and packages with flexible booking options"
        cta={<button onClick={() => {
          try { logEvent('open_hotels_search', { team: teamName, opponent: game?.opponent, partner: 'travelocity' }); } catch {}
          const travelocityUrl = `https://prf.hn/click/camref:1011lTcBA/creativeref:1100l110704#destination=${encodeURIComponent(extractCityFromVenue(game.venue))}&checkin=${formatDateForUrl(game.date)}&checkout=${formatDateForUrl(new Date(new Date(game.date).getTime() + 24 * 60 * 60 * 1000).toISOString())}`;
          window.open(travelocityUrl, '_blank', 'noopener,noreferrer');
        }} className="text-blue-600 text-sm">Search hotels</button>}
      />
      
      {/* VRBO */}
      <ListCard
        title="VRBO"
        body="Rent entire homes and vacation rentals for your game day stay"
        cta={<button onClick={() => {
          try { logEvent('open_hotels_search', { team: teamName, opponent: game?.opponent, partner: 'vrbo' }); } catch {}
          const vrboUrl = `https://vrbo.com/affiliate/oWiAOm8#city=${encodeURIComponent(extractCityFromVenue(game.venue))}&arrival=${formatDateForUrl(game.date)}&departure=${formatDateForUrl(new Date(new Date(game.date).getTime() + 24 * 60 * 60 * 1000).toISOString())}`;
          window.open(vrboUrl, '_blank', 'noopener,noreferrer');
        }} className="text-blue-600 text-sm">Find rentals</button>}
      />
    </div>
  );

  const Tickets = () => (
    <div className="panel grid grid-cols-1 md:grid-cols-2 gap-4">
      {(recommendation.tickets || []).length === 0 && (
        <div className="border border-gray-200 rounded-xl p-4 bg-white text-sm text-gray-600">No ticket suggestions provided.</div>
      )}
      {(recommendation.tickets || []).map((t, i) => (
        <ListCard key={i}
          title={t.partner || 'Tickets'}
          body={t.suggestion}
          cta={<button
            onClick={() => {
              try { logEvent('open_tickets_search', { team: teamName, opponent: game?.opponent, partner: t.partner || 'auto' }); } catch {}
              if (typeof onOpenBooking === 'function') {
                onOpenBooking('tickets', game, teamName);
                return;
              }
              let url = '';
              const partner = (t.partner || '').toLowerCase();
              if (partner.includes('stubhub')) url = generateTicketUrl(game, teamName);
              else if (partner.includes('ticketmaster')) url = bookingUrls.ticketmaster || generateTicketmasterUrl(game, teamName);
              else if (partner.includes('vivid')) url = bookingUrls.vividseats || generateVividSeatsUrl(game, teamName);
              else if (t.link) url = t.link;
              else url = bookingUrls.tickets;
              if (url) window.open(url, '_blank', 'noopener,noreferrer');
            }}
            className="text-blue-600 text-sm"
          >Buy tickets</button>}
        />
      ))}
    </div>
  );

  const Dining = () => (
    <div className="panel space-y-4">
      {(recommendation.dining || []).length === 0 && (
        <div className="border border-gray-200 rounded-xl p-4 bg-white text-sm text-gray-600">No dining suggestions provided.</div>
      )}
      {(recommendation.dining || []).map((cat, i) => (
        <ListCard key={i}
          title={cat.category}
          body={cat.description}
          withAccent
          cta={<div>
            <div className="text-xs text-gray-500 mb-2">{(cat.venues || []).slice(0, 3).join(' • ')}</div>
            <button onClick={() => {
              try { logEvent('open_restaurants_search', { team: teamName, opponent: game?.opponent }); } catch {}
              // Open Google Maps directly for restaurant bookings using proper URL generator
              const restaurantUrl = generateRestaurantUrl(game);
              window.open(restaurantUrl, '_blank', 'noopener,noreferrer');
            }} className="text-blue-600 text-sm">Find reservations</button>
          </div>}
        />
      ))}
    </div>
  );

  const Transport = () => (
    <div className="panel">
      <p className="text-sm text-gray-700 mb-3">Use our smart search to book rental cars. Rideshare and public transit tips will appear here when available.</p>
      <button onClick={() => {
        try { logEvent('open_rentalcars_search', { team: teamName, opponent: game?.opponent }); } catch {}
        if (typeof onOpenBooking === 'function') {
          onOpenBooking('rentalCars', game, teamName);
        } else if (bookingUrls.rentalCars) {
          window.open(bookingUrls.rentalCars, '_blank', 'noopener,noreferrer');
        }
      }} className="text-blue-600 text-sm">Open rental cars</button>
    </div>
  );

  const Summary = () => {
    const matchup = recommendation.gameInfo?.matchup || `${teamName} vs ${game.opponent}`;
    const dateStr = recommendation.gameInfo?.date || game.date;
    const venueStr = recommendation.gameInfo?.venue || game.venue;

    const shareText = `🏈 Trip Itinerary — ${matchup}\n\nDate: ${dateStr}\nVenue: ${venueStr}\n\nCosts:\n• Flights: ${formatCurrency(parseAmount(costs.flights))}\n• Hotels: ${formatCurrency(parseAmount(costs.hotels))}\n• Tickets: ${formatCurrency(parseAmount(costs.tickets))}\n• Other: ${formatCurrency(parseAmount(costs.other))}\n\nTotal: ${formatCurrency(totalCost)}\n\nLinks:\n• Flights: ${bookingUrls.flights || '—'}\n• Hotels.com: ${bookingUrls.hotels || '—'}\n• Travelocity: https://prf.hn/click/camref:1011lTcBA/creativeref:1100l110704\n• VRBO: https://vrbo.com/affiliate/oWiAOm8\n• Tickets: ${bookingUrls.tickets || '—'}\n• Dining: ${bookingUrls.restaurants || '—'}\n• Rental Cars: ${bookingUrls.rentalCars || '—'}\n`;

    const handleShare = async () => {
      try {
        if (navigator.share) {
          await navigator.share({ title: 'Trip Itinerary', text: shareText });
          return;
        }
      } catch {}
      try {
        await navigator.clipboard.writeText(shareText);
        alert('✅ Itinerary copied to clipboard!');
      } catch {
        alert('Copied failed. Please copy manually.');
      }
    };

    const handlePrint = () => {
      const w = window.open('', '_blank');
      if (!w) return;
      const html = `<!doctype html><html><head><meta charset="utf-8"/><title>Trip Itinerary</title>
        <style>
          body { font-family: system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif; padding: 24px; color: #111827; }
          h1 { font-size: 20px; margin: 0 0 8px 0; }
          h2 { font-size: 16px; margin: 16px 0 8px 0; }
          .grid { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; }
          .box { border: 1px solid #e5e7eb; border-radius: 8px; padding: 12px; background: #fff; }
          .label { color: #6b7280; font-size: 12px; }
          .value { font-size: 14px; font-weight: 600; }
        </style>
      </head><body>
        <h1>Trip Itinerary</h1>
        <div class="box">
          <div class="label">Matchup</div>
          <div class="value">${matchup}</div>
          <div class="label" style="margin-top:8px;">Date</div>
          <div class="value">${dateStr}</div>
          <div class="label" style="margin-top:8px;">Venue</div>
          <div class="value">${venueStr}</div>
        </div>
        <h2>Costs</h2>
        <div class="grid">
          <div class="box"><div class="label">Flights</div><div class="value">${formatCurrency(parseAmount(costs.flights))}</div></div>
          <div class="box"><div class="label">Hotels</div><div class="value">${formatCurrency(parseAmount(costs.hotels))}</div></div>
          <div class="box"><div class="label">Tickets</div><div class="value">${formatCurrency(parseAmount(costs.tickets))}</div></div>
          <div class="box"><div class="label">Other</div><div class="value">${formatCurrency(parseAmount(costs.other))}</div></div>
          <div class="box" style="grid-column:1/-1;"><div class="label">Total</div><div class="value">${formatCurrency(totalCost)}</div></div>
        </div>
        <h2>Links</h2>
        <div class="box">
          <div><span class="label">Flights:</span> ${bookingUrls.flights || '—'}</div>
          <div><span class="label">Hotels.com:</span> ${bookingUrls.hotels || '—'}</div>
          <div><span class="label">Travelocity:</span> https://prf.hn/click/camref:1011lTcBA/creativeref:1100l110704</div>
          <div><span class="label">VRBO:</span> https://vrbo.com/affiliate/oWiAOm8</div>
          <div><span class="label">Tickets:</span> ${bookingUrls.tickets || '—'}</div>
          <div><span class="label">Dining:</span> ${bookingUrls.restaurants || '—'}</div>
          <div><span class="label">Rental Cars:</span> ${bookingUrls.rentalCars || '—'}</div>
        </div>
        <script>window.onload = () => { window.print(); };</script>
      </body></html>`;
      w.document.write(html);
      w.document.close();
    };

    return (
      <div className="panel">
        <Section title="Itinerary Summary">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="p-4 bg-white rounded-lg border border-gray-200">
              <div className="text-xs text-gray-500">Matchup</div>
              <div className="text-sm font-semibold text-gray-900">{matchup}</div>
              <div className="mt-3 text-xs text-gray-500">Date</div>
              <div className="text-sm font-semibold text-gray-900">{dateStr}</div>
              <div className="mt-3 text-xs text-gray-500">Venue</div>
              <div className="text-sm font-semibold text-gray-900">{venueStr}</div>
            </div>
            <div className="p-4 bg-white rounded-lg border border-gray-200">
              <div className="text-xs text-gray-500 mb-2">Booking Links</div>
              <ul className="text-sm text-blue-600 space-y-1">
                <li><button className="underline" onClick={() => onOpenBooking ? onOpenBooking('flights', game, teamName) : bookingUrls.flights && window.open(bookingUrls.flights, '_blank', 'noopener,noreferrer')}>Flights</button></li>
                <li><button className="underline" onClick={() => onOpenBooking ? onOpenBooking('hotels', game, teamName) : bookingUrls.hotels && window.open(bookingUrls.hotels, '_blank', 'noopener,noreferrer')}>Hotels.com</button></li>
                <li><button className="underline" onClick={() => {
                  const travelocityUrl = `https://prf.hn/click/camref:1011lTcBA/creativeref:1100l110704#destination=${encodeURIComponent(extractCityFromVenue(game.venue))}&checkin=${formatDateForUrl(game.date)}&checkout=${formatDateForUrl(new Date(new Date(game.date).getTime() + 24 * 60 * 60 * 1000).toISOString())}`;
                  window.open(travelocityUrl, '_blank', 'noopener,noreferrer');
                }}>Travelocity</button></li>
                <li><button className="underline" onClick={() => {
                  const vrboUrl = `https://vrbo.com/affiliate/oWiAOm8#city=${encodeURIComponent(extractCityFromVenue(game.venue))}&arrival=${formatDateForUrl(game.date)}&departure=${formatDateForUrl(new Date(new Date(game.date).getTime() + 24 * 60 * 60 * 1000).toISOString())}`;
                  window.open(vrboUrl, '_blank', 'noopener,noreferrer');
                }}>VRBO</button></li>
                <li><button className="underline" onClick={() => onOpenBooking ? onOpenBooking('tickets', game, teamName) : bookingUrls.tickets && window.open(bookingUrls.tickets, '_blank', 'noopener,noreferrer')}>Tickets</button></li>
                <li><button className="underline" onClick={() => {
                  // Open Google Maps directly for restaurant bookings using proper URL generator
                  const restaurantUrl = generateRestaurantUrl(game);
                  window.open(restaurantUrl, '_blank', 'noopener,noreferrer');
                }}>Dining</button></li>
                <li><button className="underline" onClick={() => onOpenBooking ? onOpenBooking('rentalCars', game, teamName) : bookingUrls.rentalCars && window.open(bookingUrls.rentalCars, '_blank', 'noopener,noreferrer')}>Rental Cars</button></li>
              </ul>
            </div>
          </div>
        </Section>

        <Section title="Costs">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="p-4 bg-white rounded-lg border border-gray-200">
              <label className="text-xs text-gray-500">Flights</label>
              <input value={costs.flights} onChange={(e) => setCosts({ ...costs, flights: e.target.value })} placeholder="$0" className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg" />
              <div className="mt-3 text-xs text-gray-500">Hotels</div>
              <input value={costs.hotels} onChange={(e) => setCosts({ ...costs, hotels: e.target.value })} placeholder="$0" className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg" />
            </div>
            <div className="p-4 bg-white rounded-lg border border-gray-200">
              <label className="text-xs text-gray-500">Tickets</label>
              <input value={costs.tickets} onChange={(e) => setCosts({ ...costs, tickets: e.target.value })} placeholder="$0" className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg" />
              <div className="mt-3 text-xs text-gray-500">Other</div>
              <input value={costs.other} onChange={(e) => setCosts({ ...costs, other: e.target.value })} placeholder="$0" className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg" />
            </div>
          </div>
          <div className="mt-4 p-4 bg-gray-50 rounded-lg border border-gray-200 flex items-center justify-between">
            <div className="text-sm text-gray-700">Total</div>
            <div className="text-lg font-semibold text-gray-900">{formatCurrency(totalCost)}</div>
          </div>
        </Section>

        <div className="flex items-center justify-end gap-3">
          <button onClick={handleShare} className="px-4 py-2 rounded-lg text-sm font-medium bg-blue-600 text-white hover:bg-blue-700">Share</button>
          <button
            onClick={handlePrint}
            className="px-4 py-2 rounded-lg text-sm font-medium text-white"
            style={{ background: `linear-gradient(135deg, ${primary} 0%, ${secondary} 100%)`, border: `1px solid ${primary}` }}
          >
            Print
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="tabbed-modal-overlay" onClick={onClose}>
      <div className="tabbed-modal" onClick={(e) => e.stopPropagation()}>
        <Header />
        <Tabs />
        <div className="flex-1 overflow-y-auto">
          {active === 'overview' && <Overview />}
          {active === 'flights' && <Flights />}
          {active === 'hotels' && <Hotels />}
          {active === 'tickets' && <Tickets />}
          {active === 'dining' && <Dining />}
          {active === 'transport' && <Transport />}
          {active === 'summary' && <Summary />}
        </div>
        <div className="p-4 border-t border-gray-200 flex items-center justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-lg text-sm font-medium text-white"
            style={{ background: `linear-gradient(135deg, ${primary} 0%, ${secondary} 100%)`, border: `1px solid ${primary}` }}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default TabbedItineraryModal;


